import CheckValid from "../../Common/CheckValid";

// The All warranties list: its filters, the body of the OMS search they make, and the words of each
// row (contract: warranty-programme-handover/release/2026-10-15/all-warranties-contract.md).
// Filters are non-personal, so they may sit in the URL; the search text (a phone, a frame number or
// a registration id) never does: it goes in the POST body only.

export const PAGE_SIZE = 50;

// "" is "any" for every filter. The service takes these values only.
export const STATUS_FILTERS = [
  "",
  "active",
  "pending_review",
  "awaiting_invoice",
  "expired",
  "cancelled",
  "superseded",
];
// Every registration is one of these three (27 Oct 2026, Sachin): the service counts an old record
// as the app that wrote it in the old OMS, and the OMS forward as the dealer app.
export const SOURCE_FILTERS = ["", "rider_app", "dealer_app", "website"];
// `none` is a registration without a live plan; the others are plan tier keys.
export const PLAN_FILTERS = ["", "none", "assure", "assure_plus"];

export const STATUS_LABEL = {
  "": "Any status",
  active: "Active",
  pending_review: "Waiting for review",
  awaiting_invoice: "Awaiting invoice",
  expired: "Expired",
  cancelled: "Cancelled",
  superseded: "Replaced",
  rejected: "Rejected",
};
export const SOURCE_LABEL = {
  "": "Any source",
  rider_app: "Connect+",
  dealer_app: "Dealer app",
  website: "Website",
  oms_forward: "Dealer app",
  migration: "Old records",
  import: "Import",
};
export const PLAN_LABEL = { "": "Any plan", none: "No plan", assure: "Assure", assure_plus: "Assure+" };

// why (Sachin, 27 Oct 2026): staff open this list to follow paid Assure+ plans, so it opens on them.
// "Any plan" is "" like the other filters, so the URL carries it as `plan=any`.
export const DEFAULT_FILTERS = { status: "", source: "", plan: "assure_plus", from: "", to: "" };
const ANY_PLAN_PARAM = "any";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const oneOf = (value, allowed, fallback = "") => (allowed.includes(value) ? value : fallback);
const isoDate = (value) => (typeof value === "string" && ISO_DATE.test(value) ? value : "");

export const filtersFromParams = (params) => {
  const from = isoDate(params.get("from"));
  const to = isoDate(params.get("to"));
  return {
    status: oneOf(params.get("status"), STATUS_FILTERS),
    source: oneOf(params.get("source"), SOURCE_FILTERS),
    plan: params.get("plan") === ANY_PLAN_PARAM ? "" : oneOf(params.get("plan"), PLAN_FILTERS, DEFAULT_FILTERS.plan),
    // A reversed range is dropped rather than sent: the service would refuse it.
    from: from && to && from > to ? "" : from,
    to,
  };
};

// Only what differs from the default, so the plain list has a plain URL.
export const filtersToParams = (filters) => {
  const out = {};
  ["status", "source", "plan", "from", "to"].forEach((key) => {
    if (key === "plan") {
      if (filters.plan !== DEFAULT_FILTERS.plan) out.plan = filters.plan || ANY_PLAN_PARAM;
    } else if (filters[key]) {
      out[key] = filters[key];
    }
  });
  return out;
};

export const hasFilters = (filters) => Object.keys(filtersToParams(filters)).length > 0;

// The body of POST /warranty/registrations/search (the country is added by warrantyApi). The
// service refuses unknown keys and empty values, so only set ones go. `q` is the trimmed search.
export const searchBody = (filters, q, cursor, limit = PAGE_SIZE) => {
  const out = { limit };
  const text = typeof q === "string" ? q.trim() : "";
  if (text) out.q = text;
  ["status", "source", "plan", "from", "to"].forEach((key) => {
    if (filters[key]) out[key] = filters[key];
  });
  if (cursor) out.cursor = cursor;
  return out;
};

// "" when the text is fine to search for; the rule lives in CheckValid.
export const searchMessage = (text) => {
  let message = "";
  CheckValid(text, {
    type: "warranty_registration_search",
    error: (m) => {
      message = m;
    },
  });
  return message;
};

// Row words -------------------------------------------------------------------------------------

const has = (table, key) => typeof key === "string" && Object.prototype.hasOwnProperty.call(table, key);

export const statusLabel = (status) => (status && has(STATUS_LABEL, status) ? STATUS_LABEL[status] : status || "-");
// An old record reads as the app that wrote it in the old OMS (`legacySource`): the dealer app
// still registers there and the hourly import copies it in (27 Oct 2026).
export const sourceLabel = (source, legacySource = null) => {
  if (source === "migration" && legacySource && has(SOURCE_LABEL, legacySource)) return SOURCE_LABEL[legacySource];
  return source && has(SOURCE_LABEL, source) ? SOURCE_LABEL[source] : source || "-";
};

export const STATUS_TONE = {
  active: "success",
  pending_review: "warning",
  awaiting_invoice: "info",
  expired: "neutral",
  rejected: "danger",
  cancelled: "danger",
  superseded: "neutral",
};
export const statusTone = (status) => STATUS_TONE[status] || "neutral";

// The cover of an active registration (none for any other status).
export const COVERAGE_LABEL = {
  active: "Cover active",
  expired: "Cover ended",
  unknown: "No purchase date yet",
  none: "No cover",
};
export const coverageLabel = (coverage) =>
  coverage && has(COVERAGE_LABEL, coverage) ? COVERAGE_LABEL[coverage] : coverage || "-";
export const coverageTone = (coverage) =>
  ({ active: "success", expired: "neutral", unknown: "warning" })[coverage] || "neutral";

export const tierLabel = (tier) => (tier && has(PLAN_LABEL, tier) ? PLAN_LABEL[tier] : tier ? String(tier) : "");

const BILLING_LABEL = { one_time: "one-time", monthly: "monthly" };
const PLAN_ENDED = ["cancelled", "refunded", "ended", "completed"];

// "No plan" or "Assure+ · monthly · active", with a tone for its pill.
export const planPill = (plan) => {
  if (!plan || !plan.tier) {
    return { text: "No plan", tone: "neutral" };
  }
  const parts = [tierLabel(plan.tier)];
  if (plan.billingMode) parts.push(BILLING_LABEL[plan.billingMode] || String(plan.billingMode));
  if (plan.status) parts.push(String(plan.status));
  const tone = plan.status === "active" ? "success" : PLAN_ENDED.includes(plan.status) ? "neutral" : "warning";
  return { text: parts.join(" · "), tone };
};

const OWNER_LABEL = { associate: "SSE", dealer: "Dealer" };

// null without a referral; else the code, who owns it, its status and its incentive (true, false,
// or null while pending).
export const referralSummary = (referral) => {
  if (!referral || !referral.code) {
    return null;
  }
  return {
    code: referral.code,
    owner: OWNER_LABEL[referral.ownerKind] || (referral.ownerKind ? String(referral.ownerKind) : "-"),
    status: referral.status || "-",
    incentive: referral.incentiveEligible === true ? true : referral.incentiveEligible === false ? false : null,
  };
};

export const saleChannelLabel = (channel) =>
  ({ website: "Website", marketplace: "Marketplace", dealer: "Dealer", b2b: "B2B", other: "Other" })[
    channel
  ] || (channel ? String(channel) : "-");
